/**
 * GuardianConsole — interactive Guardian Portfolio prototype.
 *
 * Lives inline inside /p/portfolio (replaces the static dashboard mock
 * section). All five screens (Overview, Company, CompliMind, Modules,
 * Calculator) share one client-side data store seeded from
 * `lib/guardianData.js`. State is held here at the top and threaded
 * down — no backend, no router for the inner tabs.
 *
 * Tone: posture-only, attorney-led, dual-entity language (CompliSun, Inc.
 * vs CompliSun Legal, PLC). Manager earnings never include legal work.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, Building2, Sparkles, ToggleRight, Calculator,
  ShieldCheck, AlertTriangle, CheckCircle2, Lock,
} from "lucide-react";

import { SAMPLE_COMPANIES } from "@/lib/guardianData";
import GuardianOverview from "./GuardianOverview";
import GuardianCompany from "./GuardianCompany";
import GuardianCompliMind from "./GuardianCompliMind";
import GuardianModules from "./GuardianModules";
import GuardianCalculator from "./GuardianCalculator";

const TABS = [
  { id: "overview",  label: "Portfolio",   Icon: LayoutDashboard },
  { id: "company",   label: "Company",     Icon: Building2 },
  { id: "complimind",label: "CompliMind",  Icon: Sparkles },
  { id: "modules",   label: "Modules",     Icon: ToggleRight },
  { id: "calculator",label: "Calculator",  Icon: Calculator },
];

/* deep-clone helper so demo state edits don't mutate the seed */
function clone(o) { return JSON.parse(JSON.stringify(o)); }

export default function GuardianConsole() {
  const [companies, setCompanies] = useState(() => clone(SAMPLE_COMPANIES));
  const [activeTab, setActiveTab] = useState("overview");
  const [activeCompanyId, setActiveCompanyId] = useState(SAMPLE_COMPANIES[0].id);
  const [recalcStamp, setRecalcStamp] = useState(0); // bumped on any data edit

  const activeCompany = useMemo(
    () => companies.find((c) => c.id === activeCompanyId) || companies[0],
    [companies, activeCompanyId]
  );

  // Any state mutation routes through this so we can fire the "Recalculated" toast.
  const mutate = (fn, message = "Recalculated") => {
    setCompanies((prev) => {
      const next = clone(prev);
      fn(next);
      return next;
    });
    setRecalcStamp((s) => s + 1);
    if (message) toast.success(message, { duration: 1200 });
  };

  const goToCompany = (id) => {
    setActiveCompanyId(id);
    setActiveTab("company");
  };

  return (
    <div
      data-testid="guardian-console"
      className="rounded-xl overflow-hidden border shadow-2xl"
      style={{ borderColor: "rgba(15,30,46,0.12)", background: "#0F1E2E" }}
    >
      {/* Mock browser chrome — keeps the "this is a console" framing */}
      <div className="flex items-center gap-1.5 px-4 py-2.5" style={{ background: "#0F1E2E" }}>
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#ff5f57" }} />
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#ffbd2e" }} />
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#28c941" }} />
        <span className="text-[10px] text-white/50 ml-3" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.16em" }}>
          guardian.complisunguardian.com / portfolio
        </span>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-white/55" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.16em" }}>
          <Lock className="h-3 w-3" /> POSTURE ONLY · PRIVILEGE WALL ENGAGED
        </span>
      </div>

      <div className="grid grid-cols-[180px_1fr] bg-white" style={{ minHeight: 680 }}>
        {/* Left rail */}
        <nav className="border-r flex flex-col py-3" style={{ borderColor: "rgba(15,30,46,0.08)", background: "#FBF4DC" }}>
          <div className="px-4 py-2 flex items-center gap-2 mb-3">
            <img src="/brand/complisun-mark.png" alt="" className="h-6 w-6 object-contain" />
            <div className="leading-tight">
              <div className="text-[11px] font-semibold" style={{ color: "#0F1E2E" }}>CompliSun</div>
              <div className="text-[8px]" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "#C98F00" }}>GUARDIAN PORTFOLIO</div>
            </div>
          </div>
          {TABS.map(({ id, label, Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                data-testid={`gtab-${id}`}
                className="flex items-center gap-2 px-4 py-2.5 text-sm transition border-l-2"
                style={{
                  background: active ? "rgba(245,180,0,0.18)" : "transparent",
                  borderColor: active ? "#F5B400" : "transparent",
                  color: active ? "#0F1E2E" : "rgba(15,30,46,0.65)",
                  fontWeight: active ? 600 : 500,
                }}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />
                {label}
              </button>
            );
          })}
          <div className="px-4 py-3 mt-auto">
            <div className="text-[9px] mb-1" style={{ fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.18em", color: "rgba(15,30,46,0.45)" }}>/ TENANT</div>
            <select
              value={activeCompanyId}
              onChange={(e) => { setActiveCompanyId(e.target.value); if (activeTab !== "company" && activeTab !== "overview") setActiveTab("company"); }}
              data-testid="gtenant-switcher"
              className="w-full text-xs px-2 py-1.5 rounded border"
              style={{ borderColor: "rgba(15,30,46,0.15)", background: "white", color: "#0F1E2E" }}
            >
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </nav>

        {/* Right pane */}
        <div className="overflow-y-auto" style={{ maxHeight: 680 }}>
          {activeTab === "overview" && (
            <GuardianOverview
              companies={companies}
              onSelectCompany={goToCompany}
            />
          )}
          {activeTab === "company" && (
            <GuardianCompany
              company={activeCompany}
              recalcStamp={recalcStamp}
              onResolveScreening={(rid) => mutate((cs) => {
                const c = cs.find((x) => x.id === activeCompanyId);
                const r = c.screening.find((x) => x.id === rid);
                if (r) r.resolved = true;
              }, "Flag resolved · score recalculated")}
              onCompleteTask={(tid) => mutate((cs) => {
                const c = cs.find((x) => x.id === activeCompanyId);
                const t = c.tasks.find((x) => x.id === tid);
                if (t) { t.status = "done"; t.overdue = false; }
              }, "Task completed · score recalculated")}
              onBumpTraining={() => mutate((cs) => {
                const c = cs.find((x) => x.id === activeCompanyId);
                const remaining = (c.training.assigned || 0) - (c.training.completed || 0);
                const bump = Math.min(remaining, Math.max(1, Math.ceil(remaining / 3)));
                c.training.completed = (c.training.completed || 0) + bump;
              }, "Training updated · score recalculated")}
              onVendorAction={(vname, action) => mutate((cs) => {
                const c = cs.find((x) => x.id === activeCompanyId);
                const v = c.vendors.find((x) => x.name === vname);
                if (!v) return;
                if (action === "soc2_received") v.soc2 = "current";
                if (action === "reattest_sent") {
                  v.reattestDue = new Date(Date.now() + 365 * 86400 * 1000).toISOString().slice(0, 10);
                  v.status = "monitored";
                }
                if (action === "remediation") v.status = "remediation";
              }, "Vendor updated · score recalculated")}
            />
          )}
          {activeTab === "complimind" && (
            <GuardianCompliMind companies={companies} onSelectCompany={goToCompany} />
          )}
          {activeTab === "modules" && (
            <GuardianModules
              companies={companies}
              onToggleModule={(coId, mk) => mutate((cs) => {
                const c = cs.find((x) => x.id === coId);
                if (mk === "base") return; // base is locked
                const i = c.activeModules.indexOf(mk);
                if (i >= 0) c.activeModules.splice(i, 1);
                else c.activeModules.push(mk);
              }, "Module updated · domains recalculated")}
              onSetTier={(coId, tier) => mutate((cs) => {
                const c = cs.find((x) => x.id === coId);
                c.engagementTier = tier;
              }, "Engagement tier updated")}
              onSetPortfolioDefault={(modules, tier) => mutate((cs) => {
                for (const c of cs) {
                  c.activeModules = Array.from(new Set(["base", ...modules]));
                  c.engagementTier = tier;
                }
              }, "Applied to all companies")}
            />
          )}
          {activeTab === "calculator" && (
            <GuardianCalculator companies={companies} />
          )}
        </div>
      </div>

      {/* Footer note */}
      <div className="px-5 py-3 text-[10px] flex items-center justify-between" style={{ background: "#0F1E2E", color: "rgba(253,251,245,0.6)", fontFamily: "ui-monospace, SF Mono, monospace", letterSpacing: "0.14em" }}>
        <span className="inline-flex items-center gap-2"><ShieldCheck className="h-3 w-3" style={{ color: "#F5B400" }} /> COMPLISUN, INC. · PLATFORM + COMPLIANCE</span>
        <span className="inline-flex items-center gap-2"><Lock className="h-3 w-3" style={{ color: "#F5B400" }} /> POSTURE ONLY · LEGAL AVAILABLE AS AN ADD-ON</span>
      </div>
    </div>
  );
}
